import { useState, useEffect, useCallback, useRef } from 'react';
import { config } from "../../config";
import axios from 'axios';
import { api } from '../../api';

const BusSidebarDetails = ({ bus, pickup, drop, cancelPolicy, reviews: initialReviews = {}, isOpen = false, onClose }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(isOpen);
  const [activeTab, setActiveTab] = useState('overview');
  const tabsContainerRef = useRef(null);
  const contentRef = useRef(null);
  
  // Reviews state
  const [reviewsData, setReviewsData] = useState({
    list: initialReviews?.data || [],
    avg: initialReviews?.avg || 0,
    total: initialReviews?.total || 0,
    distribution: initialReviews?.distribution || {1:0, 2:0, 3:0, 4:0, 5:0}
  });
  
  // Reviews modal state
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [hasMoreReviews, setHasMoreReviews] = useState(true);
  const [reviewsPage, setReviewsPage] = useState(1);
  const reviewsContainerRef = useRef(null);

  // Tab configurations
  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📋' },
    { id: 'reviews', label: 'Reviews', icon: '⭐', count: reviewsData?.total || 0 },
    { id: 'specs', label: 'Specifications', icon: '⚙️' },
    { id: 'pickup', label: 'Pickup Points', icon: '📍', count: pickup?.length },
    { id: 'drop', label: 'Drop Points', icon: '🏁', count: drop?.length },
    { id: 'cancellation', label: 'Cancellation', icon: '❌', count: cancelPolicy?.length },
    { id: 'gallery', label: 'Gallery', icon: '🖼️', count: bus?.gallery?.length },
    { id: 'features', label: 'Features', icon: '⭐', count: bus?.features?.length },
  ];

  // Auto-scroll active tab into view
  useEffect(() => {
    if (tabsContainerRef.current && isDrawerOpen) {
      const activeElement = tabsContainerRef.current.querySelector(`[data-tab="${activeTab}"]`);
      if (activeElement) {
        activeElement.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [activeTab, isDrawerOpen]);

  // Reset scroll when tab changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  // isOpen prop changes पर state update
  useEffect(() => {
    setIsDrawerOpen(isOpen);
  }, [isOpen]);

  // Reset to overview tab when drawer opens
  useEffect(() => {
    if (isDrawerOpen) {
      setActiveTab('overview');
    }
  }, [isDrawerOpen]);

  const handleClose = useCallback(() => {
    setIsDrawerOpen(false);
    if (onClose) onClose();
  }, [onClose]);

  const handleBackdropClick = (e) => {
    if (e.target.id === 'drawer-backdrop') {
      handleClose();
    }
  };

  // Escape key से बंद करने के लिए
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isDrawerOpen) {
        handleClose();
      }
    };

    if (isDrawerOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isDrawerOpen, handleClose]);

  // Drawer खुला हो तो body scroll disable करें
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isDrawerOpen]);

  // Reviews modal के लिए body scroll handle
  useEffect(() => {
    if (showReviewsModal) {
      document.body.style.overflow = 'hidden';
    } else if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [showReviewsModal, isDrawerOpen]);

  // Load reviews function
  const loadReviews = async (page = 1, reset = false) => {
    if (!bus?.id) return;
    
    setLoadingReviews(true);
    try {
      const response = await axios.get(api.getReviews+'/'+bus.id, {
        params: {
          page: page,
          per_page: 10
        }
      });

      if (response.data.status) {
        const newReviews = response.data.data?.data || response.data.data || [];
        const responseData = response.data.data || {};
        
        if (reset) {
          setReviewsData(prev => ({
            ...prev,
            list: newReviews,
            avg: responseData.avg || prev.avg,
            total: responseData.total || prev.total,
            distribution: responseData.distribution || prev.distribution
          }));
        } else {
          setReviewsData(prev => ({
            ...prev,
            list: [...prev.list, ...newReviews],
            avg: responseData.avg || prev.avg,
            total: responseData.total || prev.total,
            distribution: responseData.distribution || prev.distribution
          }));
        }
        
        // Check if there are more reviews
        const currentPage = responseData.current_page || 1;
        const lastPage = responseData.last_page || 1;
        
        setHasMoreReviews(currentPage < lastPage);
        
        if (page > 1) {
          setReviewsPage(page);
        }
      } else {
        console.warn('API returned false status:', response.data.message);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
      // Fallback to initial reviews if API fails
      if (reset) {
        setReviewsData({
          list: initialReviews?.data || [],
          avg: initialReviews?.avg || 0,
          total: initialReviews?.total || 0,
          distribution: initialReviews?.distribution || {1:0, 2:0, 3:0, 4:0, 5:0}
        });
      }
    } finally {
      setLoadingReviews(false);
    }
  };

  // Open reviews modal
  const openReviewsModal = () => {
    setShowReviewsModal(true);
    if (reviewsData.list.length === 0) {
      loadReviews(1, true);
    }
  };

  // Close reviews modal
  const closeReviewsModal = () => {
    setShowReviewsModal(false);
    setReviewsPage(1);
  };

  // Handle scroll for infinite loading
  useEffect(() => {
    const container = reviewsContainerRef.current;
    if (!container || !showReviewsModal || loadingReviews || !hasMoreReviews) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop <= clientHeight + 100) {
        loadReviews(reviewsPage + 1);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [showReviewsModal, loadingReviews, hasMoreReviews, reviewsPage]);

  // Render reviews distribution
  const renderReviewsDistribution = () => {
    if (!reviewsData?.distribution) return null;
    
    const distribution = reviewsData.distribution;
    const total = reviewsData.total || 1;
    
    return (
      <div className="space-y-2">
        {[5, 4, 3, 2, 1].map((rating) => (
          <div key={rating} className="flex items-center">
            <div className="w-10 text-sm font-medium text-gray-600">{rating} ★</div>
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden mx-3">
              <div 
                className="h-full bg-yellow-500"
                style={{ 
                  width: `${((distribution[rating] || 0) / total) * 100}%` 
                }}
              />
            </div>
            <div className="w-10 text-sm text-gray-500 text-right">
              {distribution[rating] || 0}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Bus Header with Image */}
            <div className="relative rounded-xl overflow-hidden bg-gradient-to-r from-blue-50 to-indigo-50 p-5">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
              
              <div className="relative z-10">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${bus?.ac ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {bus?.ac ? 'AIR CONDITIONED' : 'NON-AC'}
                      </span>
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                        {bus?.type?.toUpperCase() || 'BUS'}
                      </span>
                    </div>
                    
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">{bus?.name || 'N/A'}</h2>
                    {/* <p className="text-gray-600">Bus No: <span className="font-semibold">{bus?.bus_number || 'N/A'}</span></p> */}
                  </div>
                  
                  <div className="text-center bg-white rounded-xl p-3 shadow-sm">
                    <div className="text-xs text-gray-500 mb-1">Rating</div>
                    <div className="flex items-center justify-center">
                      <span className="text-yellow-500 text-xl mr-1">★</span>
                      <span className="text-xl font-bold">{reviewsData?.avg || '0'}</span>
                      <span className="text-gray-400 text-sm ml-1">/5</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {reviewsData?.total || 0} reviews
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-3">
                    <div className="text-xs text-gray-500">Model Year</div>
                    <div className="text-lg font-bold text-gray-900">{bus?.model || '2024'}</div>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-3">
                    <div className="text-xs text-gray-500">Deck</div>
                    <div className="text-lg font-bold text-gray-900">{bus?.deck}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="text-2xl font-bold text-blue-600">{pickup?.length || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Pickup Points</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="text-2xl font-bold text-green-600">{drop?.length || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Drop Points</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="text-2xl font-bold text-amber-600">{bus?.features?.length || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Features</div>
              </div>
            </div>

            {/* Reviews Summary */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <span className="mr-2">⭐</span> Customer Reviews
                  </h3>
                  <p className="text-sm text-gray-500">Based on {reviewsData?.total || 0} reviews</p>
                </div>
                <div className="flex items-center">
                  <div className="text-2xl font-bold mr-2">{reviewsData?.avg || '4.5'}</div>
                  <div className="text-yellow-500 text-xl">★</div>
                </div>
              </div>
              
              {renderReviewsDistribution()}
              
              <button
                onClick={openReviewsModal}
                className="w-full mt-4 py-2.5 bg-gradient-to-r from-yellow-50 to-amber-50 text-amber-700 font-medium rounded-lg hover:from-yellow-100 hover:to-amber-100 transition-all"
              >
                View All Reviews ({reviewsData?.total || 0})
              </button>
            </div>

            {/* Quick Links */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                <span className="mr-2">🔍</span> Quick Access
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <button 
                  onClick={() => setActiveTab('specs')}
                  className="p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors text-left"
                >
                  <div className="flex items-center">
                    <span className="text-blue-600 mr-2">⚙️</span>
                    <span className="text-sm font-medium text-gray-900">Specifications</span>
                  </div>
                </button>
                <button 
                  onClick={() => setActiveTab('cancellation')}
                  className="p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors text-left"
                >
                  <div className="flex items-center">
                    <span className="text-red-600 mr-2">❌</span>
                    <span className="text-sm font-medium text-gray-900">Cancellation</span>
                  </div>
                </button>
                <button 
                  onClick={() => setActiveTab('gallery')}
                  className="p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors text-left"
                >
                  <div className="flex items-center">
                    <span className="text-purple-600 mr-2">🖼️</span>
                    <span className="text-sm font-medium text-gray-900">Gallery</span>
                  </div>
                </button>
                <button 
                  onClick={() => setActiveTab('features')}
                  className="p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors text-left"
                >
                  <div className="flex items-center">
                    <span className="text-green-600 mr-2">⭐</span>
                    <span className="text-sm font-medium text-gray-900">Features</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        );

      case 'reviews':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-5">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Customer Reviews</h2>
              <p className="text-gray-600 text-sm">Based on {reviewsData?.total || 0} reviews</p>
            </div>

            {/* Overall Rating */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-3xl font-bold">{reviewsData?.avg || '0'}</div>
                  <div className="flex items-center mt-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span 
                        key={star} 
                        className={`text-xl ${star <= Math.floor(reviewsData?.avg || 0) ? 'text-yellow-500' : 'text-gray-300'}`}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={openReviewsModal}
                  className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                >
                  View All Reviews
                </button>
              </div>

              {/* Reviews Distribution */}
              {renderReviewsDistribution()}
            </div>

            {/* Review Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-yellow-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-amber-600">{reviewsData?.total || 0}</div>
                <div className="text-sm text-amber-700 mt-1">Total Reviews</div>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {reviewsData?.distribution?.[5] || 0}
                </div>
                <div className="text-sm text-green-700 mt-1">5 Star Reviews</div>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {Math.round((reviewsData?.avg || 4.5) * 20)}%
                </div>
                <div className="text-sm text-blue-700 mt-1">Satisfaction</div>
              </div>
            </div>

            {/* Most Recent Reviews (First 3) */}
            {reviewsData.list.slice(0, 3).map((review, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-bold">
                        {review.user?.name?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {review.user?.name || 'Anonymous User'}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {review.created_at ? new Date(review.created_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center bg-amber-50 px-3 py-1 rounded-full">
                    <span className="text-amber-600 mr-1">★</span>
                    <span className="font-semibold text-amber-700">{review.rate || 0}</span>
                  </div>
                </div>
                <p className="text-gray-700">{review.review || 'No comment provided.'}</p>
              </div>
            ))}

            {reviewsData?.total > 3 && (
              <button
                onClick={openReviewsModal}
                className="w-full py-3 bg-gray-50 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                Load More Reviews ({reviewsData?.total - 3} more)
              </button>
            )}

            {reviewsData.list.length === 0 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">⭐</span>
                </div>
                <p className="text-gray-500">No reviews yet</p>
              </div>
            )}
          </div>
        );

      case 'specs':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Technical Specifications</h2>
              <p className="text-gray-600 text-sm">Complete details about the bus</p>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center mb-2">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-blue-600">🚌</span>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Bus Name</div>
                      <div className="font-semibold text-gray-900">{bus?.name || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center mb-2">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-green-600">🔢</span>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Bus Number</div>
                      <div className="font-semibold text-gray-900">{bus?.bus_number || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center mb-2">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-purple-600">📅</span>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Model Year</div>
                      <div className="font-semibold text-gray-900">{bus?.model || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center mb-2">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-amber-600">🪑</span>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Total Seats</div>
                      <div className="font-semibold text-gray-900">{bus?.seats || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Additional Information</h3>
                <div className="space-y-2">
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Bus Type</span>
                    <span className="font-medium">{bus?.type?.toUpperCase() || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">AC / Non-AC</span>
                    <span className={`font-medium ${bus?.ac ? 'text-green-600' : 'text-gray-600'}`}>
                      {bus?.ac ? 'AIR CONDITIONED' : 'NON-AIR CONDITIONED'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Rating</span>
                    <span className="font-medium flex items-center">
                      <span className="text-yellow-500 mr-1">★</span>
                      {bus?.rating || '0'}/5
                    </span>
                  </div>
                  {bus?.vendor && (
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Operator</span>
                      <span className="font-medium">{bus.vendor.company || 'N/A'}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'cancellation':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-5">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Cancellation Policy</h2>
              <p className="text-gray-600 text-sm">Understand refund rules and charges</p>
            </div>

            {/* Policy Overview */}
            <div className="bg-white border border-red-100 rounded-xl p-4">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-red-600 text-xl">⏰</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Important Points</h3>
                  <ul className="mt-2 space-y-1 text-sm text-gray-600">
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>
                      Cancellation charges vary based on time before departure
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>
                      No refund if cancelled after bus departure
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>
                      Service charges may apply
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Cancellation Table */}
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <div className="bg-gray-50 px-4 py-3">
                <h3 className="font-semibold text-gray-900">Cancellation Charges</h3>
                <p className="text-xs text-gray-500">Based on cancellation time</p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left p-3 text-xs font-semibold text-gray-700 whitespace-nowrap">Time Before Travel</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-700 whitespace-nowrap">Refund Amount</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-700 whitespace-nowrap">Return %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {cancelPolicy?.map((policy, index) => (
                      <tr key={index} className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                        <td className="p-3 text-sm text-gray-900 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`w-2 h-2 rounded-full mr-2 ${
                              index < 3 ? 'bg-green-500' : 
                              index < 6 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}></div>
                            {policy.time_before_travel}
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            policy.percentage === "100%" ? 'bg-green-100 text-green-800' :
                            policy.percentage === "0%" ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {policy.return}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`font-semibold ${
                            policy.percentage === "100%" ? 'text-green-600' :
                            policy.percentage === "0%" ? 'text-red-600' :
                            'text-yellow-600'
                          }`}>
                            {policy.percentage}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Legend */}
            <div className="grid grid-cols-3 gap-2">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-xs text-gray-600">High Refund</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                <span className="text-xs text-gray-600">Medium Refund</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                <span className="text-xs text-gray-600">Low Refund</span>
              </div>
            </div>

            {/* Notes */}
            <div className="bg-blue-50 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                <span className="mr-2">📝</span> Important Notes
              </h4>
              <ul className="space-y-1 text-xs text-gray-600">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Refunds are processed within 5-7 business days
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Cancellation can be done through app or website
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Partial cancellations may have different rules
                </li>
              </ul>
            </div>
          </div>
        );

      case 'pickup':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Pickup Points</h2>
              <p className="text-gray-600 text-sm">{pickup?.length || 0} available pickup locations</p>
            </div>

            <div className="space-y-3">
              {pickup?.map((point, index) => (
                <div key={point.id || index} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-green-300 transition-colors">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-green-600 font-bold">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <h3 className="font-semibold text-gray-900 mb-1">{point.name || 'Pickup Point'}</h3>
                        <span className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap">
                          {point.time || '--:--'}
                        </span>
                      </div>
                      {point.address && (
                        <p className="text-sm text-gray-600 mt-1">{point.address}</p>
                      )}
                      <div className="flex items-center mt-2 text-xs text-gray-500">
                        <span className="mr-3 flex items-center">
                          <span className="mr-1">📍</span> Pickup
                        </span>
                        <span className="flex items-center">
                          <span className="mr-1">⏰</span> Report 15 mins before
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-blue-50 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                <span className="mr-2">💡</span> Pickup Instructions
              </h4>
              <ul className="space-y-1 text-xs text-gray-600">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Please arrive at the pickup point 15 minutes before departure
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Carry a valid ID proof and booking confirmation
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Boarding pass will be sent to your registered mobile number
                </li>
              </ul>
            </div>
          </div>
        );

      case 'drop':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Drop Points</h2>
              <p className="text-gray-600 text-sm">{drop?.length || 0} available drop locations</p>
            </div>

            <div className="space-y-3">
              {drop?.map((point, index) => (
                <div key={point.id || index} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-purple-300 transition-colors">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-purple-600 font-bold">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <h3 className="font-semibold text-gray-900 mb-1">{point.name || 'Drop Point'}</h3>
                        <span className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap">
                          {point.time || '--:--'}
                        </span>
                      </div>
                      {point.address && (
                        <p className="text-sm text-gray-600 mt-1">{point.address}</p>
                      )}
                      <div className="flex items-center mt-2 text-xs text-gray-500">
                        <span className="mr-3 flex items-center">
                          <span className="mr-1">📍</span> Drop
                        </span>
                        <span className="flex items-center">
                          <span className="mr-1">🎯</span> Final Destination
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'gallery':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Bus Gallery</h2>
              <p className="text-gray-600 text-sm">{bus?.gallery?.length || 0} photos available</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {bus?.gallery?.map((image, index) => (
                <div key={index} className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                  <img
                    src={`${config.baseUrl}/${image}`}
                    alt={`Bus ${bus.name || ''} ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>

            {(!bus?.gallery || bus.gallery.length === 0) && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">📷</span>
                </div>
                <p className="text-gray-500">No images available</p>
              </div>
            )}
          </div>
        );

      case 'features':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-5">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Amenities & Features</h2>
              <p className="text-gray-600 text-sm">{bus?.features?.length || 0} amenities available</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {bus?.features?.map((feature, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-colors">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-blue-600">
                        {feature.toLowerCase().includes('ac') ? '❄️' :
                         feature.toLowerCase().includes('wifi') ? '📶' :
                         feature.toLowerCase().includes('seat') ? '🪑' :
                         feature.toLowerCase().includes('charging') ? '🔋' :
                         feature.toLowerCase().includes('water') ? '💧' :
                         feature.toLowerCase().includes('blanket') ? '🛏️' :
                         feature.toLowerCase().includes('entertain') ? '🎬' : '⭐'}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{feature}</span>
                  </div>
                </div>
              ))}
            </div>

            {(!bus?.features || bus.features.length === 0) && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">⭐</span>
                </div>
                <p className="text-gray-500">No features listed</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Render Reviews Modal
  const renderReviewsModal = () => {
    if (!showReviewsModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={closeReviewsModal}
        />
        
        {/* Modal */}
        <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Customer Reviews</h2>
                <p className="text-sm text-gray-500">
                  {reviewsData?.total || 0} reviews • Avg: {reviewsData?.avg || '4.5'} ★
                </p>
              </div>
              <button
                onClick={closeReviewsModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div 
            ref={reviewsContainerRef}
            className="overflow-y-auto h-[calc(90vh-120px)]"
          >
            <div className="p-6">
              {/* Overall Stats */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-center">
                    <div className="text-4xl font-bold">{reviewsData?.avg || '4.5'}</div>
                    <div className="flex items-center justify-center mt-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span 
                          key={star} 
                          className={`text-xl ${star <= Math.floor(reviewsData?.avg || 4.5) ? 'text-yellow-500' : 'text-gray-300'}`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1 ml-6">
                    {renderReviewsDistribution()}
                  </div>
                </div>
              </div>

              {/* Reviews List */}
              <div className="space-y-4">
                {Array.isArray(reviewsData.list) && reviewsData.list.map((review, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-blue-600 font-bold text-lg">
                            {review.user?.name?.charAt(0) || 'U'}
                          </span>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {review.user?.name || 'Anonymous User'}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {review.created_at ? new Date(review.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            }) : 'Date not available'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center bg-amber-50 px-3 py-1 rounded-full">
                        <span className="text-amber-600 mr-1">★</span>
                        <span className="font-semibold text-amber-700">{review.rate || 0}</span>
                      </div>
                    </div>
                    
                        <div className="mb-2">
                      <h5 className="font-medium text-gray-900 mb-1">
                        {review.rate >= 4.5 ? 'Excellent Experience' :
                        review.rate >= 4.0 ? 'Very Good Experience' :
                        review.rate >= 3.5 ? 'Good Experience' :
                        review.rate >= 3.0 ? 'Average Experience' :
                        review.rate >= 2.0 ? 'Below Average' : 'Poor Experience'}
                      </h5>
                      <p className="text-gray-700">{review.review || 'No comment provided.'}</p>
                    </div>
                                  
                    {review.reply && (
                      <div className="mt-3 pl-4 border-l-4 border-blue-200 bg-blue-50 p-3 rounded-r-lg">
                        <div className="flex items-center mb-1">
                          <span className="text-blue-600 font-sm">Operator Response</span>
                          <span className="text-xs text-gray-500 ml-2">
                            {review.created_at ? new Date(review.updated_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                        <p className="text-gray-700">{review.reply}</p>
                      </div>
                    )}
                  </div>
                ))}

                {/* Loading Indicator */}
                {loadingReviews && (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                )}

                {/* No More Reviews */}
                {!hasMoreReviews && reviewsData.list.length > 0 && (
                  <div className="text-center py-4 text-gray-500">
                    No more reviews to load
                  </div>
                )}

                {/* No Reviews */}
                {!loadingReviews && reviewsData.list.length === 0 && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-2xl">⭐</span>
                    </div>
                    <p className="text-gray-500">No reviews yet</p>
                    <p className="text-sm text-gray-400 mt-1">Be the first to review this bus!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      {isDrawerOpen && (
        <div
          id="drawer-backdrop"
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Drawer */}
      <div
        className={`fixed top-0 right-0 z-50 h-screen w-full sm:max-w-lg transition-transform duration-300 ease-out ${
          isDrawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col bg-white shadow-2xl">
          {/* Header */}
          <div className="flex-shrink-0 border-b border-gray-200 bg-white">
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors mr-3"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Bus Details</h2>
                  <p className="text-xs text-gray-500">Complete information & policies</p>
                </div>
              </div>
            </div>

            {/* Tabs Navigation */}
            <div className="px-6 pb-1">
              <div 
                ref={tabsContainerRef}
                className="flex space-x-1 overflow-x-auto scrollbar-hide py-2"
              >
                {tabs.map((tab) => {
                  if (
                    (tab.id === 'pickup' && (!pickup || pickup.length === 0)) ||
                    (tab.id === 'drop' && (!drop || drop.length === 0)) ||
                    (tab.id === 'gallery' && (!bus?.gallery || bus.gallery.length === 0)) ||
                    (tab.id === 'features' && (!bus?.features || bus.features.length === 0)) ||
                    (tab.id === 'cancellation' && (!cancelPolicy || cancelPolicy.length === 0)) ||
                    (tab.id === 'reviews' && (!reviewsData?.total || reviewsData.total === 0))
                  ) {
                    return null;
                  }
                  
                  return (
                    <button
                      key={tab.id}
                      data-tab={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                        activeTab === tab.id
                          ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <span className="mr-2 text-sm">{tab.icon}</span>
                      {tab.label}
                      {tab.count > 0 && (
                        <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                          activeTab === tab.id 
                            ? 'bg-white/20 text-white' 
                            : 'bg-gray-200 text-gray-700'
                        }`}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-hidden">
            <div 
              ref={contentRef}
              className="h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
            >
              <div className="p-6">
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Modal */}
      {renderReviewsModal()}
    </>
  );
};

export default BusSidebarDetails;